import React, { useState, useEffect } from "react";
import './EditAbout.css';
import { useLocation } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import Modal from 'react-modal';

Modal.setAppElement('#root');

// State and city data for USA and India
const countryData = {
  US: {
    name: "United States",
    states: {
      CA: {
        name: "California",
        cities: [/*"Los Angeles", "San Francisco", "San Diego",*/"San Jose"]
      },
    /* NY: {
        name: "New York",
        cities: ["New York City", "Buffalo", "Rochester"]
      },
      TX: {
        name: "Texas",
        cities: ["Houston", "Dallas", "Austin"]
      }
      // Add more states as needed */
    }
  },
  IN: {
    name: "India",
    states: {
     /* MH: {
        name: "Maharashtra",
        cities: ["Mumbai", "Pune", "Nagpur"]
      },
      DL: {
        name: "Delhi",
        cities: ["New Delhi", "Noida", "Gurgaon"]
      },*/
      KA: {
        name: "Karnataka",
        cities: ["Udupi", "Kundapura", "Karkala", "Byndoor", "Brahmavar", "Hebri", "Kaup"]
      }
      // Add more states as needed
    }
  }
};

// ── Module-level cache ────────────────────────────────────────────────────────
// Fallback if EditStore's prefetched aboutData isn't ready yet.
const _aboutCache = new Map();
const ABOUT_TTL = 5 * 60 * 1000; // 5 minutes

function EditAbout() {
    const { aboutData, pageId: ctxPageId, token: ctxToken } = useOutletContext() || {};

    const [pageType, setPageType] = useState("");
    const location = useLocation();
    const [content, setContent] = useState({
        paragraphs: [],
        website: "",
        verified_page: "",
        industry: "",
        company: "",
        headquarter: "",
        founded: "",
        specialties: "",
        address: "",
        country: "",
        state: "",
        city: "",
        zipCode: ""
    });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editMode, setEditMode] = useState('create');
    const [currentParagraphIndex, setCurrentParagraphIndex] = useState(null);
    
    const [formData, setFormData] = useState({
        paragraphs: [],
        website: "",
        verified_page: "",
        industry: "",
        company: "",
        headquarter: "",
        founded: "",
        specialties: "",
        address: "",
        country: "",
        state: "",
        city: "",
        zipCode: ""
    });

    // Available countries (only USA and India)
    const [countries] = useState([
        { code: "US", name: "United States" },
        { code: "IN", name: "India" }
    ]);
    
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    
    const [isLoadingContent, setIsLoadingContent] = useState(true);
    const [pageId] = useState(ctxPageId || localStorage.getItem('pageId') || null);
    const [hasAboutInfo, setHasAboutInfo] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';
    const token = localStorage.getItem('token');


    // Update states when country changes
    useEffect(() => {
        if (formData.country && countryData[formData.country]) {
            const countryStates = Object.entries(countryData[formData.country].states).map(
                ([code, state]) => ({ code, name: state.name })
            );
            setStates(countryStates);
        } else {
            setStates([]);
        }
        // Reset state and city when country changes
        setFormData(prev => ({
            ...prev,
            state: "",
            city: ""
        }));
    }, [formData.country]);

    // Update cities when state changes
    useEffect(() => {
        if (formData.country && formData.state && countryData[formData.country]?.states[formData.state]) {
            setCities(countryData[formData.country].states[formData.state].cities);
        } else {
            setCities([]);
        }
        // Reset city when state changes
        setFormData(prev => ({
            ...prev,
            city: ""
        }));
    }, [formData.state]);

    // ── Load about data ───────────────────────────────────────────────────────
    useEffect(() => {
        // ── Fast path: EditStore already prefetched this data ─────────────
        if (aboutData) {
            applyData(aboutData);
            setIsLoadingContent(false);
            return;
        }

        // ── Fallback: fetch directly if context data not ready yet ────────
        if (!pageId) {
            setIsLoadingContent(false);
            setHasAboutInfo(false);
            return;
        }

        // Check module-level cache before hitting network
        const cached = _aboutCache.get(pageId);
        if (cached && Date.now() - cached.ts < ABOUT_TTL) {
            applyData(cached.data);
            setIsLoadingContent(false);
            return;
        }

        fetchAboutPage();
    }, [aboutData, pageId]);

    // Normalise raw API response into component state
    const applyData = (data) => {
        const normalized = {
            paragraphs:    data.paragraphs    || [],
            website:       data.website       || "",
            verified_page: data.verified_page || "",
            industry:      data.industry      || "",
            company:       data.company       || "",
            headquarter:   data.headquarter   || "",
            founded:       data.founded       || "",
            specialties:   data.specialties   || "",
            address:       data.address       || "",
            country:       data.country       || "",
            state:         data.state         || "",
            city:          data.city          || "",
            zipCode:       data.zipcode !== undefined ? (data.zipcode || "") : (data.zipCode || ""),
        };
        setContent(normalized);
        setFormData(normalized);
        setPageType(data.page_type || "");
        setHasAboutInfo(true);
    };

    const fetchAboutPage = async () => {
        setIsLoadingContent(true);
        const t0 = performance.now();
        const controller = new AbortController();
        try {
            const response = await fetch(`${API_URL}/get_about_page?pageid=${pageId}`, {
                signal: controller.signal,
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            const elapsed = performance.now() - t0;
            if (elapsed > 2000) {
                console.error(`[EditAbout][SLOW] get_about_page took ${(elapsed/1000).toFixed(2)}s (>2s) pageid=${pageId}`);
            }

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            
            const data = await response.json();
            if (data.message === "No about page found") {
                setHasAboutInfo(false);
                return;
            }

            _aboutCache.set(pageId, { data, ts: Date.now() });
            applyData(data);
        } catch (error) {
            if (error.name === 'AbortError') return;
            console.error("Error fetching About page:", error);
            setHasAboutInfo(false);
        } finally {
            setIsLoadingContent(false);
        }
    };

    const handleSaveAll = async () => {
        try {
            const response = await fetch(`${API_URL}/save_about_page`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    pageid: pageId,
                    ...formData,
                }),
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            
            const data = await response.json();
            alert(data.message || "Saved successfully");
            setContent(formData);
            // Bust cache so re-navigation shows fresh data
            _aboutCache.delete(pageId);
            setIsModalOpen(false);
            setHasAboutInfo(true);
        } catch (error) {
            console.error("Error saving about page:", error);
            alert("Failed to save changes");
        }
    };

    const openEditModal = () => {
        setFormData(content);
        setEditMode('edit');
        setIsModalOpen(true);
    };

    const openCreateModal = () => {
        setFormData({
            paragraphs: [],
            website: "",
            verified_page: "",
            industry: "",
            company: "",
            headquarter: "",
            founded: "",
            specialties: "",
            address: "",
            country: "",
            state: "",
            city: "",
            zipCode: ""
        });
        setEditMode('create');
        setIsModalOpen(true);
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleParagraphChange = (index, value) => {
        const updatedParagraphs = [...formData.paragraphs];
        updatedParagraphs[index] = value;
        setFormData(prev => ({
            ...prev,
            paragraphs: updatedParagraphs
        }));
    };

    const handleAddParagraph = () => {
        setFormData(prev => ({
            ...prev,
            paragraphs: [...prev.paragraphs, ""]
        }));
        setCurrentParagraphIndex(formData.paragraphs.length);
    };

    const handleDeleteParagraph = (index) => {
        const updatedParagraphs = formData.paragraphs.filter((_, i) => i !== index);
        setFormData(prev => ({
            ...prev,
            paragraphs: updatedParagraphs
        }));
    };

    const hasContent = () => {
        return (
            content.paragraphs.length > 0 ||
            content.website ||
            content.verified_page ||
            content.industry ||
            content.company ||
            content.headquarter ||
            content.founded ||
            content.specialties ||
            content.address ||
            content.country ||
            content.state ||
            content.city ||
            content.zipCode
        );
    };

    if (isLoadingContent) {
        return (
            <div className="about_container-edit">
                <div className="about_content-edit">
                    <style>{`
                        .skel { background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                                background-size: 200% 100%; animation: skel-shimmer 1.2s infinite;
                                border-radius: 4px; }
                        @keyframes skel-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                    `}</style>
                    <div className="header-container-edit" style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
                        <div className="skel" style={{height:24, width:100}} />
                        <div className="skel" style={{height:30, width:60}} />
                    </div>
                    <div className="skel" style={{height:14, width:'100%', marginBottom:8}} />
                    <div className="skel" style={{height:14, width:'88%', marginBottom:8}} />
                    <div className="skel" style={{height:14, width:'93%', marginBottom:24}} />
                    <div className="skel" style={{height:16, width:80, marginBottom:8}} />
                    <div className="skel" style={{height:14, width:'55%', marginBottom:24}} />
                    <div className="skel" style={{height:16, width:80, marginBottom:8}} />
                    <div className="skel" style={{height:14, width:'40%', marginBottom:8}} />
                    <div className="skel" style={{height:14, width:'35%'}} />
                </div>
            </div>
        );
    }

    if (!hasAboutInfo && !isModalOpen) {
        return (
            <div className="about_container-edit">
                <div className="no-about-container">
                    <div className="header-container-edit">
                        <h2>Overview</h2>
                    </div>
                    <div className="empty-about-message">
                        <p>No about information added yet.</p>
                        <button className="add-about-btn" onClick={openCreateModal}>
                            + Add About Information
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="about_container-edit">
            <div className="about_content-edit">
                <div className="header-container-edit">
                    <h2>Overview</h2>
                    <button className="edit_button-edit" onClick={openEditModal}>
                        Edit
                    </button>
                </div>

                {!hasContent() ? (
                    <div className="no-content-message">
                        <p>No about information available.</p>
                    </div>
                ) : (
                    <>
                        {content.paragraphs.map((para, index) => (
                            <div key={index} className="paragraph-container-edit">
                                <p className="para-edit">{para}</p>
                            </div>
                        ))}

                        {pageType === "Firm" && content.website && (
                            <>
                                <h3>Website</h3>
                                <a href={content.website} target="_blank" rel="noopener noreferrer">
                                    {content.website}
                                </a>
                            </>
                        )}

                        {pageType === "Firm" && content.verified_page && (
                            <>
                                <h3>Verified Page</h3>
                                <p>{content.verified_page}</p>
                            </>
                        )}

                        {pageType === "Firm" && content.industry && (
                            <>
                                <h3>Industry</h3>
                                <p>{content.industry}</p>
                            </>
                        )}

                        {pageType === "Firm" && content.company && (
                            <>
                                <h3>Company Size</h3>
                                <p>{content.company}</p>
                            </>
                        )}

                        {pageType === "Firm" && content.founded && (
                            <>
                                <h3>Founded</h3>
                                <p>{content.founded}</p>
                            </>
                        )}

                        {pageType === "Firm" && content.headquarter && (
                            <>
                                <h3>Headquarters</h3>
                                <p>{content.headquarter}</p>
                            </>
                        )}

                        {pageType === "Firm" && content.specialties && (
                            <>
                                <h3>Specialties</h3>
                                <p>{content.specialties}</p>
                            </>
                        )}

                        {(content.address || content.city || content.state || content.zipCode || content.country) && (
                            <>
                                <h3>Address</h3>
                                <div className="address-display">
                                    {content.address && <p>{content.address}</p>}
                                    {(content.city || content.state || content.zipCode) && (
                                        <p>
                                            {content.city}{content.city && (content.state || content.zipCode) ? ', ' : ''}
                                            {content.state}{content.state && content.zipCode ? ' ' : ''}
                                            {content.zipCode}
                                        </p>
                                    )}
                                    {content.country && (
                                        <p>{countries.find(c => c.code === content.country)?.name || content.country}</p>
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            {/* Edit Modal */}
            <Modal
                isOpen={isModalOpen}
                onRequestClose={() => setIsModalOpen(false)}
                className="about-modal"
                overlayClassName="about-modal-overlay"
            >
                <div className="modal-header">
                    <h2>{editMode === 'create' ? 'Add About Information' : 'Edit About Information'}</h2>
                    <button onClick={() => setIsModalOpen(false)} className="modal-close-btn">
                        &times;
                    </button>
                </div>

                <div className="modal-content">
                    <div className="form-section">
                        <h3>Description</h3>
                        {formData.paragraphs.map((para, index) => (
                            <div key={index} className="paragraph-edit-container">
                                <textarea
                                    value={para}
                                    onChange={(e) => handleParagraphChange(index, e.target.value)}
                                    className="editable-text-edit"
                                    placeholder="Enter description paragraph"
                                />
                                <button 
                                    className="delete-btn-edit" 
                                    onClick={() => handleDeleteParagraph(index)}
                                >
                                    Delete
                                </button>
                            </div>
                        ))}
                        <button className="add-btn-edit" onClick={handleAddParagraph}>
                            Add Paragraph
                        </button>
                    </div>

                    {pageType === "Firm" && (
  <div className="form-section">
    <h3>Basic Information</h3>

    <div className="form-field">
      <label>Website</label>
      <input
        type="text"
        value={formData.website}
        onChange={(e) => handleFormChange('website', e.target.value)}
        className="editable-input-edit"
        placeholder="https://example.com"
      />
    </div>

    <div className="form-field">
      <label>Verified Date</label>
      <input
        type="date"
        value={formData.verified_page}
        onChange={(e) => handleFormChange('verified_page', e.target.value)}
        className="editable-input-edit"
      />
    </div>

    <div className="form-field">
      <label>Industry</label>
      <input
        type="text"
        value={formData.industry}
        onChange={(e) => handleFormChange('industry', e.target.value)}
        className="editable-input-edit"
        placeholder="e.g. Technology, Retail"
      />
    </div>

    <div className="form-field">
      <label>Company Size</label>
      <input
        type="text"
        value={formData.company}
        onChange={(e) => handleFormChange('company', e.target.value)}
        className="editable-input-edit"
        placeholder="e.g. 100-500 employees"
      />
    </div>

    <div className="form-field">
      <label>Founded</label>
      <input
        type="month"
        value={formData.founded}
        onChange={(e) => handleFormChange('founded', e.target.value)}
        className="editable-input-edit"
      />
    </div>

    <div className="form-field">
      <label>Headquarters</label>
      <input
        type="text"
        value={formData.headquarter}
        onChange={(e) => handleFormChange('headquarter', e.target.value)}
        className="editable-input-edit"
        placeholder="e.g. San Francisco, CA"
      />
    </div>

    <div className="form-field">
      <label>Specialties</label>
      <input
        type="text"
        value={formData.specialties}
        onChange={(e) => handleFormChange('specialties', e.target.value)}
        className="editable-input-edit"
        placeholder="e.g. E-commerce, Cloud Computing"
      />
    </div>
  </div>
)}


                    <div className="form-section">
                        <h3>Address</h3>
                        <div className="form-field">
                            <label>Street Address</label>
                            <input
                                type="text"
                                value={formData.address}
                                onChange={(e) => handleFormChange('address', e.target.value)}
                                className="editable-input-edit"
                                placeholder="123 Main St"
                            />
                        </div>

                        <div className="form-field">
                            <label>Country</label>
                            <select
                                value={formData.country}
                                onChange={(e) => handleFormChange('country', e.target.value)}
                                className="editable-input-edit"
                            >
                                <option value="">Select Country</option>
                                {countries.map((country) => (
                                    <option key={country.code} value={country.code}>
                                        {country.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-field">
                            <label>State/Province</label>
                            <select
                                value={formData.state}
                                onChange={(e) => handleFormChange('state', e.target.value)}
                                className="editable-input-edit"
                                disabled={!formData.country}
                            >
                                <option value="">Select State</option>
                                {states.map((state) => (
                                    <option key={state.code} value={state.code}>
                                        {state.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-field">
                            <label>City</label>
                            <select
                                value={formData.city}
                                onChange={(e) => handleFormChange('city', e.target.value)}
                                className="editable-input-edit"
                                disabled={!formData.state}
                            >
                                <option value="">Select City</option>
                                {cities.map((city, index) => (
                                    <option key={index} value={city}>
                                        {city}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-field">
                            <label>Zip/Postal Code</label>
                            <input
                                type="text"
                                value={formData.zipCode}
                                onChange={(e) => handleFormChange('zipCode', e.target.value)}
                                className="editable-input-edit"
                                placeholder="94105"
                            />
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="cancel-btn-edit" onClick={() => setIsModalOpen(false)}>
                        Cancel
                    </button>
                    <button className="save-btn-edit" onClick={handleSaveAll}>
                        Save Changes
                    </button>
                </div>
            </Modal>
        </div>
    );
}

export default EditAbout;